import { useEffect } from 'react';

/*
 * Keyboard-shortcuts help overlay. Discoverability for the editor's shortcuts
 * (zoom / undo / tool escape). Opened by the "?" appbar button or the `?` key;
 * closed by Esc, the close button, or clicking the backdrop. Pure presentational
 * — the parent owns the open state.
 */

const MOD = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform) ? '⌘' : 'Ctrl';

const GROUPS: ReadonlyArray<{ title: string; rows: ReadonlyArray<[string, string]> }> = [
  {
    title: 'コマンド / 検索',
    rows: [
      [`${MOD} + K`, 'コマンドパレット'],
      [`${MOD} + F`, 'ドキュメント内を検索'],
      ['?', 'このヘルプ'],
    ],
  },
  {
    title: '表示',
    rows: [
      [`${MOD} + =`, '拡大'],
      [`${MOD} + -`, '縮小'],
      [`${MOD} + 0`, '幅に合わせる'],
    ],
  },
  {
    title: '編集',
    rows: [
      [`${MOD} + Z`, '元に戻す'],
      [`${MOD} + Shift + Z`, 'やり直し'],
      ['Enter', 'テキスト / コメントを確定'],
      ['Esc', 'ツールを解除 / 入力を取消'],
    ],
  },
  {
    title: 'ツール (左レール)',
    rows: [
      ['▦', '選択 — オーバーレイの ✕ で個別削除'],
      ['✎', '付箋 — クリックでコメント入力'],
      ['🖍', 'ハイライト — ドラッグで範囲、色/不透明度を選択'],
      ['T', 'テキスト — ドラッグで枠、日本語・欧文対応'],
    ],
  },
  {
    title: 'ページ / ファイル',
    rows: [
      ['↑ / ↓', 'ページを上 / 下に移動'],
      ['＋ ページ / 削除', 'ページ挿入 / 削除予定'],
      ['ドロップ', 'PDF をドロップして開く / 画像をページにドロップ'],
    ],
  },
];

export function ShortcutsHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    // Capture phase so it beats the editor's global Esc-deselect handler.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="kbd-backdrop" onClick={onClose} role="presentation">
      <div
        className="kbd-modal"
        role="dialog"
        aria-modal="true"
        aria-label="キーボードショートカット"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kbd-modal__head">
          <h2>キーボードショートカット</h2>
          <button className="btn" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        <div className="kbd-grid">
          {GROUPS.map((g) => (
            <section key={g.title} className="kbd-group">
              <h3>{g.title}</h3>
              {g.rows.map(([keys, desc]) => (
                <div key={keys + desc} className="kbd-row">
                  <kbd className="kbd-keys">{keys}</kbd>
                  <span className="kbd-desc">{desc}</span>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
